import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
// A3 — anexo de imagem no chat (reusa componentes A2)
import ReceiptCapture from "@/components/finance/ReceiptCapture";
import ReceiptConfirm from "@/components/finance/ReceiptConfirm";
import { authFetch } from "@/lib/authFetch";

// Mobile Parity Etapa 4: ChatInputBar extraído pra componente reusável.
import { ChatInputBar } from "@/components/chat";
// Layout & Dashboard Components
import { MainLayout } from "@/components/layout";
import {
  ImmersiveVoiceOverlay,
  HeroPanel,
  NexoSuggestionCard,
  AgendaCard,
  DailyHabitCard,
  DailyMissionCard,
  RecentTransactionsCard,
} from "@/components/dashboard";
import { DashboardSummary } from "@/components/dashboard/SummaryCards";
import { Card } from "@/components/ui/card";
import { Lightbulb } from "lucide-react";
import { TransactionsListCard, WebSearchCard, CalendarEventsCard } from "@/components/DataCards";
import ChatMessage from "@/components/ChatMessage";
import TypingIndicator from "@/components/chat/TypingIndicator";
import DayDivider, { isSameDay } from "@/components/chat/DayDivider";
import MessageSuggestions from "@/components/chat/MessageSuggestions";

// Section Components
import { TasksSection, FinanceSection, RemindersSection, HabitsSection, ProjectsSection, NotesSection, CalendarSection, AutomationsSection, RotinaSection } from "@/components/sections";

// Integration & Chat Components
import { GoogleCalendarConnect } from "@/components/integrations";
import { CalendarConnectPrompt } from "@/components/chat";
// Custom Hooks
import { useAuth } from "@/hooks/useAuth";
import useJarvisChat from "@/hooks/useJarvisChat";
import useJarvisVoice from "@/hooks/useJarvisVoice";
import useDashboardData from "@/hooks/useDashboardData";

export default function NectarDashboard() {
  const { user, token } = useAuth();
  // userId vem do JWT (PrivateRoute garante que `user` está populado).
  // Sem fallback "default_user" — se algum dia user for null aqui, é melhor
  // o chat ficar em memória do que escrever em bucket compartilhado.
  const userId = user?.id || null;
  const navigate = useNavigate();
  const location = useLocation();

  // Sessão UI-2 — hash redirect de URLs legadas pra novo layout Rotina.
  // habits → rotina/habitos; reminders|calendar → rotina/agenda; projects → rotina/projetos.
  // Mantido como const fora de hook pra ser usado tanto no init state
  // quanto no listener de hashchange.
  const resolveHash = (rawHash) => {
    const h = (rawHash || "").replace(/^#/, "");
    if (!h) return { section: "home", subhash: null };
    const legacyMap = {
      habits:    "rotina/habitos",
      reminders: "rotina/agenda",
      calendar:  "rotina/agenda",
      projects:  "rotina/projetos",
      finance:   "financeiro/visao",
    };
    const remapped = legacyMap[h] || h;
    if (remapped.startsWith("rotina/") || remapped === "rotina") {
      return { section: "rotina", subhash: remapped };
    }
    if (remapped.startsWith("financeiro/") || remapped === "financeiro") {
      return { section: "finance", subhash: remapped };
    }
    return { section: remapped, subhash: null };
  };

  // Navigation — também aceita hash (#tasks) quando volta de /settings etc.
  const [activeSection, setActiveSection] = useState(() => resolveHash(window.location.hash).section);
  const [isFocusMode, setIsFocusMode] = useState(false);

  // Sincroniza hash → activeSection quando o user navega pra "/#tasks"
  // ou hits uma URL legada (#habits redireciona pra #rotina/habitos).
  useEffect(() => {
    const { section, subhash } = resolveHash(location.hash);
    if (subhash && location.hash !== `#${subhash}`) {
      // URL legada → reescreve sem polluir history
      window.history.replaceState(null, "", `#${subhash}`);
    }
    if (section && section !== activeSection) setActiveSection(section);
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sidebar: items "externos" (settings, training) navegam pra outra rota.
  // Demais items alteram a section interna.
  const handleSectionChange = useCallback((id) => {
    if (id === "settings") { navigate("/settings"); return; }
    if (id === "training") { navigate("/training"); return; }
    setActiveSection(id);
  }, [navigate]);

  // Session — persisted in localStorage. F5 reuses, "Nova conversa" resets.
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem("nexo_session_id");
    if (stored) return stored;
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("nexo_session_id", newId);
    return newId;
  });

  // Voice input handler ref (solves circular dependency: voice needs chat, chat needs voice)
  const handleVoiceInputRef = useRef(null);
  const voiceInputCallback = useCallback((text) => {
    if (handleVoiceInputRef.current) handleVoiceInputRef.current(text);
  }, []);

  // Dashboard data
  const { dashboardData, briefing, fetchDashboardData } = useDashboardData();

  // Shared isLoading ref for voice to prevent duplicates
  const chatLoadingRef = useRef(false);

  // Voice — single instance (no audio analysis hooks — orb is CSS-driven)
  const v = useJarvisVoice({ onVoiceInput: voiceInputCallback, chatLoadingRef, authToken: token });

  // Start a fresh voice conversation with a new session (no old context)
  const handleStartConversation = useCallback(() => {
    // FIX iOS — warmup AudioContext + autoplay policy DENTRO do user gesture
    // do tap no mic. Sem isso o greeting/TTS subsequente cai em silent
    // channel em iPhone com silent switch + volume baixo do botão.
    v.warmupAudio?.();
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newId);
    localStorage.setItem("nexo_session_id", newId);
    // Overlay imersivo abre via useEffect que observa v.isConversationActive
    // — funciona tanto pra clique no mic quanto pra wake word.
    v.startConversation();
  }, [v]);

  // ===== Fase E5 — modo imersivo (fullscreen overlay) =====
  const [isImmersiveMode, setIsImmersiveMode] = useState(false);

  // 🔧 Bug fix — wake word "nexo" chama v.startConversation() direto, sem
  // passar pelo handleStartConversation. Abrir o overlay via effect garante
  // que ambos os caminhos (clique mic + wake word) acionem o modo imersivo.
  useEffect(() => {
    if (v.isConversationActive && !isImmersiveMode) {
      setIsImmersiveMode(true);
    }
    // Quando a conversa é encerrada manualmente (endConversation),
    // deixamos o auto-close cuidar — não fecha aqui pra não cortar o
    // fade-out da última resposta.
  }, [v.isConversationActive, isImmersiveMode]);

  // VOICE-UNIFICAR — fechar overlay (ESC/X/backdrop) NUNCA mata TTS em
  // andamento. Se o user fechou enquanto Nexo ainda fala, o audio segue
  // ate o fim natural; soh encerramos a conversa de fato (parar STT,
  // resetar VAD) quando state nao for SPEAKING.
  //
  // O overlay permanece aberto durante toda a conversa (listening ->
  // processing -> speaking -> listening...). Mostra apenas o status fixo
  // ("Escutando..." / "Processando..." / "Nexo esta falando"), nao
  // renderiza o texto da resposta. O painel atras recebe pergunta+
  // resposta+datacard normalmente; quando o user fecha manualmente
  // (ESC/X/backdrop) ele ve tudo no painel — e o TTS em curso, se houver,
  // termina naturalmente porque closeOverlayKeepTTS nao mata audio.
  const handleImmersiveClose = useCallback(() => {
    setIsImmersiveMode(false);
    if (v.conversationStatus === "speaking") {
      v.closeOverlayKeepTTS?.();
    } else {
      v.endConversation();
    }
  }, [v]);

  // Chat
  const chat = useJarvisChat({
    userId,
    sessionId,
    voiceEnabled: v.voiceEnabled,
    speakResponse: v.speakResponse,
    fetchDashboardData,
    // BUGFIX-LATENCY-V3 — quando o user está em conversa de voz,
    // useJarvisChat força streaming + pre-fetch da 1ª frase pra TTS.
    voiceConversationActive: v.isConversationActive,
  });

  // Sync chat loading state to voice ref
  useEffect(() => {
    chatLoadingRef.current = chat.isLoading;
  }, [chat.isLoading]);

  // ── A3 — Anexo de imagem (despesa por foto) no chat ────────────────
  // Reusa 100% o fluxo da A2: <ReceiptCapture> → <ReceiptConfirm> → POST
  // /api/finance/transactions. NÃO chama o LLM nem cria rota nova.
  // Após registrar, adiciona bolha da IA via chat.setMessages.
  const [chatAttachStep, setChatAttachStep] = useState(null); // "capture"|"confirm"|null
  const [chatAttachExtracted, setChatAttachExtracted] = useState(null);
  const [chatAttachNote, setChatAttachNote] = useState("");   // texto preservado do input
  const [chatAttachSubmitting, setChatAttachSubmitting] = useState(false);

  const openChatAttach = useCallback(() => {
    // FIX iOS — warmup AudioContext dentro do user gesture do paperclip.
    // Quando a despesa é confirmada, o TTS de confirmação ("Anotei mercado
    // R$ 85,50") chega segundos depois — warmup garante que toca.
    v.warmupAudio?.();
    // Preserva o texto digitado no input — vira `note` no upload e é
    // restaurado se o user cancelar.
    setChatAttachNote(chat.inputText || "");
    chat.setInputText("");
    setChatAttachExtracted(null);
    setChatAttachStep("capture");
  }, [chat, v]);

  const closeChatAttach = useCallback(() => {
    if (chatAttachSubmitting) return;
    // Cancelar — restaura texto que estava no input.
    chat.setInputText(chatAttachNote || "");
    setChatAttachStep(null);
    setChatAttachExtracted(null);
    setChatAttachNote("");
  }, [chat, chatAttachNote, chatAttachSubmitting]);

  const registerExpenseFromChat = useCallback(async (payload) => {
    // POST direto pra /api/finance/transactions — mesmo path da A2.
    // Resposta pode trazer { created: false, possible_duplicate }; se
    // sim, mostra confirm nativo e tenta de novo com skip flag.
    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    const _post = (skip) =>
      authFetch(
        `${API_URL}/api/finance/transactions${skip ? "?skip_duplicate_check=true" : ""}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

    setChatAttachSubmitting(true);
    try {
      let res = await _post(false);
      let body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error("Erro ao registrar despesa.");
        return;
      }
      if (body?.created === false && body?.possible_duplicate) {
        const ex = body.possible_duplicate.existing || {};
        const proceed = window.confirm(
          `Você já registrou R$ ${(ex.amount ?? 0).toFixed(2)} em ` +
          `"${ex.description || "sem descrição"}" em ${ex.date}. ` +
          "Confirma que é uma transação NOVA?",
        );
        if (!proceed) return;
        res = await _post(true);
        body = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error("Erro ao registrar (retry).");
          return;
        }
      }
      // Sucesso → fecha modal + bolha da IA + refresh do dashboard.
      const valor = `R$ ${Number(payload.amount).toLocaleString("pt-BR", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })}`;
      const merchant = (payload.description || "essa despesa").slice(0, 60);
      const cat = payload.category || "Outros";
      const replyText = `Pronto, anotei ${valor} em ${merchant} (${cat}).`;
      const assistantMessage = {
        id: `srv-a3-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        text: replyText,
        isUser: false,
        timestamp: new Date().toISOString(),
        status: "delivered",
        actions: [],
        suggestions: [],
        datacard: null,
        project_setup: null,
      };
      chat.setMessages((prev) => [...prev, assistantMessage]);
      try { fetchDashboardData?.(); } catch { /* */ }
      setChatAttachStep(null);
      setChatAttachExtracted(null);
      setChatAttachNote("");
    } catch (e) {
      toast.error("Erro ao registrar despesa.");
    } finally {
      setChatAttachSubmitting(false);
    }
  }, [chat, fetchDashboardData]);

  // "Nova conversa" — limpa msgs E rotaciona sessionId pra desconectar
  // do conversation_id antigo no backend. F5 sozinho preserva sessão.
  const handleNewChat = useCallback(() => {
    chat.clearMessages();
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newId);
    localStorage.setItem("nexo_session_id", newId);
  }, [chat]);

  // Wire voice input → chat.sendMessage
  // Pipeline is ALREADY LOCKED by onresult in useJarvisVoice when this fires.
  // speakResponse (called inside sendMessage) handles unlock when TTS finishes.
  const voiceSendingRef = useRef(false);
  useEffect(() => {
    handleVoiceInputRef.current = async (text) => {
      if (!text.trim() || voiceSendingRef.current) return;
      voiceSendingRef.current = true;
      v.setTranscript("");
      // Pipeline is already locked by onresult → no need to call lockPipeline
      // Status is already "processing" set by onresult
      try {
        // VERSION-INDICATOR item 4 — marca origin="voice" pra ativar gate
        // S7 no backend (ato destrutivo via STT exige reconfirmação texto).
        await chat.sendMessage(text, { origin: "voice" });
        // speakResponse was called fire-and-forget inside sendMessage
        // It will unlock the pipeline when TTS finishes
      } catch (err) {
        // If sendMessage failed, speakResponse was never called → unlock manually
        v.unlockPipeline(true);
        v.setConversationStatus("idle");
      } finally {
        voiceSendingRef.current = false;
      }
    };
  }, [chat, v]);

  // Scroll to bottom on new messages
  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.messages, chat.isLoading]);

  // Handle OAuth params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get("google_error");
    const googleConnected = params.get("google_connected");
    const email = params.get("email");
    if (googleError) { toast.error(decodeURIComponent(googleError)); window.history.replaceState({}, document.title, window.location.pathname); }
    if (googleConnected === "true") { toast.success(`Google Calendar conectado! ${email ? `(${decodeURIComponent(email)})` : ""}`); window.history.replaceState({}, document.title, window.location.pathname); }
  }, []);

  // Quick actions
  const handleQuickAction = useCallback((actionId) => {
    const actions = { task: "criar uma nova tarefa", finance: "mostrar minhas despesas", organize: "organizar meu dia" };
    if (actions[actionId]) chat.sendMessage(actions[actionId]);
  }, [chat]);

  // Calendar helper
  const isCalendarNotConnectedMessage = (text) => {
    if (!text) return false;
    const l = text.toLowerCase();
    return l.includes("conectar sua conta do google calendar") || l.includes("conectar google calendar") || (l.includes("precisa conectar") && l.includes("calendar"));
  };

  // ===== RENDER HELPERS =====

  // Sessão UI-1 — home renderizado em 6 linhas com stagger 80ms entre rows.
  const handleTalkToNexo = (prePrompt) => {
    if (v.voiceOverlayOpen) {
      v.endConversation();
      return;
    }
    handleStartConversation();
    if (typeof prePrompt === "string" && prePrompt.trim()) {
      // FIN-2 T12: dispara o prompt contextual logo após abrir overlay.
      // delay curto pra overlay terminar de montar antes da resposta.
      setTimeout(() => {
        try { chat.sendMessage(prePrompt); } catch (_) { /* noop */ }
      }, 350);
    }
  };

  const renderHome = () => {
    const insights = Array.isArray(briefing?.insights) ? briefing.insights : [];
    return (
      <div className="relative min-h-screen overflow-hidden" data-testid="dashboard-home-canvas">
        {/* UI-1.5 — Ambient layers (sutis, atrás do conteúdo).
            grid-texture + 2 radial gradients dão profundidade tech sem
            competir com os cards. pointer-events-none pra não interferir
            em cliques. */}
        <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "var(--ambient-bg-radial)" }}
          aria-hidden="true"
        />
        {/* Glow secundário emanando da posição esquerda alta (alinhado
            com o NexoCore na L1 do Hero). */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: 0, left: 0,
            width: "60%", height: "60%",
            background: "radial-gradient(circle at 25% 30%, rgba(0,212,255,0.10), transparent 50%)",
          }}
          aria-hidden="true"
        />

        <div className="relative px-4 md:px-8 py-6 md:py-8 space-y-6 md:space-y-8 max-w-[1280px] mx-auto w-full">
        {/* L1 — Hero */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.0, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <HeroPanel
            state={v.conversationStatus}
            onMicClick={handleTalkToNexo}
            briefing={briefing}
            transcript={v.transcript}
            isVoiceActive={v.voiceOverlayOpen}
            vadSpeaking={v.vadSpeaking}
          />
        </motion.section>

        {/* L2 — 3 KPIs */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <DashboardSummary data={dashboardData} onCardClick={handleSectionChange} />
        </motion.section>

        {/* L3 — Sugestão (2 cols) + Agenda (1 col) */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          <div className="lg:col-span-2">
            <NexoSuggestionCard
              briefing={briefing}
              onAction={chat.sendMessage}
              onNavigate={handleSectionChange}
              onTalk={handleTalkToNexo}
            />
          </div>
          <div className="lg:col-span-1">
            <AgendaCard
              userId={userId}
              onConnectClick={() => handleSectionChange("calendar")}
              onSeeAllClick={() => handleSectionChange("calendar")}
            />
          </div>
        </motion.section>

        {/* L4 — Hábito do Dia + Missão do Dia */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          <DailyHabitCard onNavigate={handleSectionChange} onAfterCheckin={fetchDashboardData} />
          <DailyMissionCard
            done={dashboardData?.habitsCheckedToday || 0}
            total={dashboardData?.habitsTotalToday || 0}
          />
        </motion.section>

        {/* L5 — Extrato */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <RecentTransactionsCard onSeeAllClick={() => handleSectionChange("finance")} />
        </motion.section>

        {/* L6 — Insights (omitido se vazio) */}
        {insights.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.40, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <Card variant="elevated" className="glass-card glass-card-purple glow-hover p-6 md:p-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-brand-purple/10 flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-brand-purple" aria-hidden="true" />
                </div>
                <span
                  className="text-caption font-semibold uppercase text-brand-purple/80"
                  style={{ letterSpacing: "0.12em" }}
                >
                  Insights da semana
                </span>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="insights-list">
                {insights.slice(0, 4).map((text, i) => (
                  <li
                    key={i}
                    className="bg-brand-purple/5 border border-brand-purple/15 rounded-lg p-4 text-sm text-foreground-secondary"
                  >
                    {text}
                  </li>
                ))}
              </ul>
            </Card>
          </motion.section>
        )}

        {/* Padding final pra não colidir com input bar mobile */}
        <div className="h-24 md:h-12" aria-hidden="true" />
        </div>
      </div>
    );
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case "home":
        return renderHome();
      case "rotina": return <RotinaSection />;
      case "tasks": return <TasksSection onSendMessage={chat.sendMessage} />;
      case "finance": return <FinanceSection onMicClick={handleTalkToNexo} />;
      case "calendar": return <CalendarSection />;
      case "reminders": return <RemindersSection />;
      case "habits": return <HabitsSection />;
      case "projects": return <ProjectsSection />;
      case "notes": return <NotesSection />;
      case "automations": return <AutomationsSection />;
      case "training":
        return (
          <div className="p-8 space-y-8">
            <div><h2 className="font-display text-2xl font-bold text-white mb-2">Configurações e Integrações</h2><p className="text-slate-400">Conecte serviços externos para expandir as capacidades do Nexo.</p></div>
            <div className="space-y-4"><h3 className="text-lg font-medium text-white">Integrações</h3><GoogleCalendarConnect userId={userId} /></div>
            <div className="mt-8 p-4 bg-nectar-card/50 border border-nectar-border rounded-2xl"><h3 className="text-lg font-medium text-white mb-2">Modo Treino</h3><p className="text-sm text-slate-500">Ensine o Nexo a entender melhor suas preferências. Em desenvolvimento...</p></div>
          </div>
        );
      default:
        return (<div className="p-8"><h2 className="font-display text-2xl font-bold text-white mb-6 capitalize">{activeSection}</h2><p className="text-slate-400">Conteúdo de {activeSection} em desenvolvimento...</p></div>);
    }
  };

  const renderContextPanelContent = () => {
    // BUGFIX-CHAT-SCROLL: render TODAS as mensagens carregadas (até
    // HISTORY_CACHE_CAP=200 do useJarvisChat). Slice(-8) anterior
    // cortava o histórico antes do CSS overflow-y:auto do ContextPanel
    // ter chance de rolar — user via só 8 últimas, sem acesso ao
    // resto via scroll up.
    const visible = chat.messages;
    return (
      <div>
        {visible.map((msg, idx, arr) => {
          const prev = arr[idx - 1];
          const showDivider =
            msg.timestamp && (!prev?.timestamp || !isSameDay(msg.timestamp, prev.timestamp));
          return (
            <Fragment key={msg.id || idx}>
              {showDivider && <DayDivider date={new Date(msg.timestamp)} />}
              <ChatMessage msg={msg} index={idx} prevMsg={prev} nextMsg={arr[idx + 1]} />
              {!msg.isUser && msg.suggestions?.length > 0 && (
                <MessageSuggestions
                  suggestions={msg.suggestions}
                  onSendMessage={chat.sendMessage}
                />
              )}
            </Fragment>
          );
        })}
        {chat.isLoading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  // ===== MAIN RENDER =====
  // Texto de boas-vindas do overlay — usa briefing ou fallback simples.
  const immersiveGreeting = briefing?.context_message || "Pronto pra te ouvir.";

  // Mobile Parity Epic Etapa 4: ChatInputBar único, passado pro MainLayout.
  // Aparece sempre que showContextPanel=true (que é controlado por
  // chat.messages.length > 0). Em ambos modos (desktop sidebar e mobile
  // bottom-sheet) renderiza no rodapé do ContextPanel.
  const chatInputBar = (
    <ChatInputBar
      chat={chat}
      voice={v}
      isFocusMode={isFocusMode}
      onToggleFocusMode={() => setIsFocusMode(!isFocusMode)}
      onMicClick={handleStartConversation}
      onAttachClick={openChatAttach}
      chatAttachSubmitting={chatAttachSubmitting}
    />
  );

  return (
    <>
      {/* Fase E5 — overlay imersivo de voz. Renderizado SEMPRE mas só visível
          quando isImmersiveMode=true. Interação por voz continua funcionando
          atrás dele (o hook useJarvisVoice não é alterado pelo overlay). */}
      <ImmersiveVoiceOverlay
        isOpen={isImmersiveMode}
        state={v.conversationStatus}
        vadSpeaking={v.vadSpeaking}
        transcript={v.transcript}
        greetingText={immersiveGreeting}
        onClose={handleImmersiveClose}
        debugProps={{
          prewarmState: v.prewarmState,
          isOutputSpeaker: v.isOutputSpeaker,
          // Snapshot vivo: lê window.__nexoVoiceDebug() exposto pelo
          // useJarvisVoice. setInterval(150ms) dentro do painel.
          debugSnapshot: () => (typeof window !== "undefined" && window.__nexoVoiceDebug
            ? window.__nexoVoiceDebug()
            : null),
        }}
      />

      <MainLayout activeSection={activeSection} onSectionChange={handleSectionChange} showContextPanel={chat.messages.length > 0} contextPanelContent={renderContextPanelContent()} messages={chat.messages} isFocusMode={isFocusMode} isConversationMode={v.voiceOverlayOpen} confirmations={chat.confirmations} onNewChat={handleNewChat} chatInput={chatInputBar}>
        {renderSectionContent()}

        {/* Mobile Parity Epic Etapa 4: drawer mobile-chat e chat input
            inline DELETADOS. O chat agora vive em UMA fonte só dentro do
            ContextPanel (visível em desktop sidebar direita e em mobile
            via bottom-sheet do chat-trigger da TopBar). ChatInputBar é
            componente extraído passado pro MainLayout via prop chatInput. */}

        {/* A3 — modal de anexo de imagem no chat (reusa A2). */}
        <AnimatePresence>
          {chatAttachStep && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeChatAttach}
              data-testid="chat-attach-modal"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md"
              >
                {chatAttachStep === "capture" && (
                  <ReceiptCapture
                    note={chatAttachNote}
                    onExtracted={(ex) => {
                      setChatAttachExtracted(ex);
                      setChatAttachStep("confirm");
                    }}
                    onClose={closeChatAttach}
                  />
                )}
                {chatAttachStep === "confirm" && chatAttachExtracted && (
                  <ReceiptConfirm
                    extracted={chatAttachExtracted}
                    isSubmitting={chatAttachSubmitting}
                    onCancel={closeChatAttach}
                    onSubmit={registerExpenseFromChat}
                  />
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </MainLayout>
    </>
  );
}
